import { useEffect, useState } from "react";
import axios from "axios";
import { ChevronDown, Archive } from "lucide-react";

/**
 * Public preview of the NEW Task-2 admin Product Editor additions —
 * read-only, no login required. Shows exactly what the owner will see
 * inside the real /admin?tab=products edit dialog for a Supply-tier
 * product: the Supply price tier dropdown, kit vial count + mg-per-vial
 * inputs, and the universal archive toggle.
 *
 * Reads real tiers from the PUBLIC /api/supply/tiers endpoint so the
 * dropdown reflects the tiers the owner actually created.
 * Route: /preview/admin-product-editor
 */

const API = process.env.REACT_APP_BACKEND_URL;
const OXBLOOD = "#4A0E17";
const CHARCOAL = "#1a1a1a";

export default function PreviewAdminProductEditor() {
  const [tiers, setTiers] = useState([]);
  const [selectedTier, setSelectedTier] = useState("");
  const [kitCount, setKitCount] = useState(3);
  const [strength, setStrength] = useState("10");
  const [archived, setArchived] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/supply/tiers`).then(({ data }) => {
      setTiers(data);
      if (data.length > 0) setSelectedTier(data[0].id);
    }).catch(() => {});
  }, []);

  const inputCls = "w-full border rounded-md px-3 py-2 text-[14px] bg-white focus:outline-none focus:ring-1";
  const labelCls = "text-[11px] uppercase tracking-[0.22em] font-mono font-bold";

  return (
    <div className="min-h-screen bg-bone">
      <div className="max-w-[900px] mx-auto px-6 lg:px-8 py-10">
        <p className="inline-block rounded-sm px-2 py-1 font-mono uppercase tracking-[0.22em] text-[10px] mb-4" style={{ background: `${OXBLOOD}12`, color: OXBLOOD }}>
          Read-only preview · admin auth stripped
        </p>
        <h1 className="font-sans font-bold text-[36px] leading-none tracking-[-0.01em] mb-2" style={{ color: OXBLOOD, fontFamily: "Manrope, ui-sans-serif, system-ui" }}>
          Product Editor · Task 2
        </h1>
        <p className="text-[14px] mb-8" style={{ color: CHARCOAL, opacity: 0.75 }}>
          Preview of the new controls that appear inside <span className="font-mono">/admin?tab=products</span> when editing a Supply-tier product. The dropdown below pulls from your real tier list. In the live admin, changes are saved to the product doc; here everything is read-only.
        </p>

        {/* Fake surrounding form context — matches the real edit dialog card style */}
        <div className="rounded-2xl border bg-white shadow-sm overflow-hidden" style={{ borderColor: "#e6e2dc" }}>
          <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: "#e6e2dc", background: "#faf8f4" }}>
            <div>
              <p className={labelCls} style={{ color: CHARCOAL, opacity: 0.7 }}>Editing product</p>
              <p className="text-[15px] font-semibold" style={{ color: CHARCOAL }}>Sample Supply Product</p>
            </div>
            <span className="inline-flex items-center text-[12px] font-mono uppercase tracking-widest px-3 py-1.5 rounded-full bg-bone border" style={{ borderColor: CHARCOAL, color: CHARCOAL }}>
              Research Supply
            </span>
          </div>

          <div className="p-6 space-y-5">
            {/* Supply price tier card — the star of Task 2 */}
            <div className="rounded-2xl border p-4" style={{ borderColor: `${OXBLOOD}33`, background: `${OXBLOOD}0d` }}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className={labelCls} style={{ color: OXBLOOD }}>Supply price tier</p>
                  <p className="text-[12px] uppercase tracking-[0.22em] font-mono mt-1" style={{ color: CHARCOAL, opacity: 0.7 }}>
                    Every product on this tier is priced identically. No variants, no volume discount.
                  </p>
                </div>
                <a
                  href="/preview/admin-supply-tiers"
                  className="text-[11px] uppercase tracking-[0.22em] font-mono px-3 py-1.5 rounded-full border transition-colors"
                  style={{ borderColor: OXBLOOD, color: OXBLOOD }}
                >
                  Manage tiers ↗
                </a>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className={labelCls} style={{ color: CHARCOAL, opacity: 0.8 }}>Assigned tier</label>
                  <div className="relative mt-1">
                    <select
                      value={selectedTier}
                      onChange={(e) => setSelectedTier(e.target.value)}
                      className={`${inputCls} appearance-none pr-8`}
                      style={{ borderColor: `${OXBLOOD}30`, color: CHARCOAL }}
                    >
                      <option value="">— None —</option>
                      {tiers.map((t) => (
                        <option key={t.id} value={t.id} disabled={!t.active}>
                          {t.label} · ${t.price.toFixed(2)}{!t.active ? " (inactive)" : ""}
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={16} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: OXBLOOD, opacity: 0.6 }} />
                  </div>
                  {tiers.length === 0 && (
                    <p className="text-[12px] mt-1 font-mono" style={{ color: OXBLOOD, opacity: 0.8 }}>
                      No tiers found. Create one in <a href="/preview/admin-supply-tiers" className="underline">Manage tiers</a>.
                    </p>
                  )}
                </div>
                <div>
                  <label className={labelCls} style={{ color: CHARCOAL, opacity: 0.8 }}>Vials per kit</label>
                  <input
                    type="number"
                    min="1"
                    value={kitCount}
                    onChange={(e) => setKitCount(e.target.value)}
                    className={`${inputCls} mt-1`}
                    style={{ borderColor: `${OXBLOOD}30`, color: CHARCOAL }}
                    placeholder="3"
                  />
                </div>
                <div>
                  <label className={labelCls} style={{ color: CHARCOAL, opacity: 0.8 }}>Mg per vial</label>
                  <input
                    type="text"
                    value={strength}
                    onChange={(e) => setStrength(e.target.value)}
                    className={`${inputCls} mt-1`}
                    style={{ borderColor: `${OXBLOOD}30`, color: CHARCOAL }}
                    placeholder="e.g. 5, 10, 7.5"
                  />
                </div>
              </div>

              {/* Live preview of what the buyer sees */}
              <div className="mt-4 rounded-lg border-dashed border p-3 bg-white/60" style={{ borderColor: `${OXBLOOD}22` }}>
                <p className={labelCls} style={{ color: CHARCOAL, opacity: 0.6 }}>Buyer sees</p>
                <p className="text-[14px] mt-1" style={{ color: CHARCOAL }}>
                  <span className="font-mono tabular-nums font-bold" style={{ color: OXBLOOD }}>
                    ${(tiers.find(t => t.id === selectedTier)?.price ?? 0).toFixed(2)}
                  </span>
                  <span className="mx-1.5" style={{ color: CHARCOAL, opacity: 0.5 }}>·</span>
                  {kitCount || 3} × {strength || "?"}mg vials
                </p>
              </div>
            </div>

            {/* Archive toggle */}
            <div className="flex items-center gap-3 pt-1">
              <button
                type="button"
                onClick={() => setArchived((s) => !s)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                style={{ background: archived ? OXBLOOD : "rgba(26,26,26,0.3)" }}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-bone transition-transform ${archived ? "translate-x-6" : "translate-x-1"}`} />
              </button>
              <div className="flex items-center gap-2">
                {archived && <Archive size={15} style={{ color: OXBLOOD }} />}
                <div>
                  <p className="text-sm font-medium" style={{ color: CHARCOAL }}>
                    {archived ? "Archived — hidden from catalog" : "Live"}
                  </p>
                  <p className="text-[13px]" style={{ color: CHARCOAL, opacity: 0.75 }}>
                    {archived
                      ? "Buyers can't see this product. Order history still resolves the name/SKU."
                      : "Product appears on the public catalog (respecting draft/active flags)."}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 rounded-lg border p-4 bg-white/60" style={{ borderColor: `${OXBLOOD}20` }}>
          <p className={labelCls} style={{ color: OXBLOOD, opacity: 0.8 }}>What ships with Task 2</p>
          <ul className="mt-2 space-y-1 text-[13px]" style={{ color: CHARCOAL }}>
            <li>• Backend fields on <span className="font-mono">Product</span>: <span className="font-mono">supply_tier_id, kit_vial_count, kit_vial_strength_mg</span></li>
            <li>• Tier delete is blocked while any product references it</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
